import {
  ChangeDetectionStrategy,
  Component,
  Input,
  ViewEncapsulation,
  forwardRef,
  ChangeDetectorRef,
  ElementRef,
  SimpleChanges,
  OnChanges,
  Optional,
  SkipSelf,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import mixinDisabled from '../../mixins/disabled';
import { PhxContextService, PHOENIX_CONTEXTS } from '../../services/phx-context-service';

export type PhxRadioSelectSize = 'medium' | 'extra-small';

export interface IPhxRadioSelectOption {
  label: string;
  value: string | number;
};

const baseRadioSelectMixin = mixinDisabled(
  class {
    constructor() {}
  }
);

@Component({
  selector: 'phx-radio-select',
  styleUrls: ['./phx-radio-select.scss'],
  templateUrl: './phx-radio-select.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  inputs: [
    'disabled'
  ],
  host: {
    '[class.phx-disabled]': 'disabled',
    '[class.phx-radio-select-md]': `phxSize === 'medium'`,
    '[class.phx-radio-select-xs]': `phxSize === 'extra-small'`,
    '[class.phx-filter]': 'phxStyleType === PHOENIX_CONTEXTS.FILTER',
  },
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => PhxRadioSelectComponent),
    multi: true
  }]
})
export class PhxRadioSelectComponent extends baseRadioSelectMixin implements ControlValueAccessor, OnChanges {
  @Input() public phxSize: PhxRadioSelectSize = 'medium';
  @Input() public phxOptions: IPhxRadioSelectOption[] = [];
  @Input() public phxValue: string | number = null;
  public readonly PHOENIX_CONTEXTS = PHOENIX_CONTEXTS;
  public phxStyleType: PHOENIX_CONTEXTS;
  private radioOptionsArray: HTMLInputElement[];
  onChange: Function = () => {};
  onTouched: Function = () => {};

  constructor(
    private cdr:ChangeDetectorRef,
    private elementRef: ElementRef,
    @Optional() @SkipSelf() contextService: PhxContextService
    ) {
    super();
    this.phxStyleType = contextService?.getType();
  }

  ngOnChanges(changes: SimpleChanges) {
    const { phxOptions } = changes;
    if (phxOptions && phxOptions.currentValue !== phxOptions.previousValue) {
      setTimeout(() => this.updateOptionElementsArray());
    }
  }

  private updateOptionElementsArray(): void {
    const radioOptions: NodeListOf<HTMLInputElement> = this.elementRef.nativeElement.querySelectorAll('.phx-radio-select-input');
    this.radioOptionsArray = Array.from(radioOptions);
  }

  private handleRadioButtonNavigation(event: KeyboardEvent, focusPrevious: boolean = false): void {
    event.preventDefault();
    const currentIndex = this.radioOptionsArray.findIndex(option => option === event.target);
    if (currentIndex === -1) return;
    if (focusPrevious) {
      if (currentIndex === 0) {
        this.radioOptionsArray[this.radioOptionsArray.length - 1].focus();
      } else {
        this.radioOptionsArray[currentIndex - 1].focus();
      }
    } else {
      if (currentIndex === this.radioOptionsArray.length - 1) {
        this.radioOptionsArray[0].focus();
      } else {
        this.radioOptionsArray[currentIndex + 1].focus();
      }
    }
  }

  public handleKeyDown(event: KeyboardEvent): void {
    switch (event.code) {
      case 'Space':
      case 'Enter':
        event.preventDefault();
        (event.target as HTMLInputElement).click();
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        this.handleRadioButtonNavigation(event);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        this.handleRadioButtonNavigation(event, true);
        break;
    }
  }

  public handleValueChange(value: any): void {
    if (value === this.phxValue) return;
    this.phxValue = value;
    this.onChange(value);
    this.cdr.markForCheck();
  }

  public writeValue(value: any): void {
    this.phxValue = value;
    this.cdr.markForCheck();
  }

  registerOnChange(fn): void {
    this.onChange = fn;
  }

  registerOnTouched(fn): void {
    this.onTouched = fn;
  }
}
